import React, {useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

/* Constants */
import {
  VIDEO_TYPES,
  HTML5_VIDEO_NOT_SUPPORTED_TEXT
} from './VideoViewConstants'

/* Components */
import Overlay from '../Overlay/Overlay'

/* Utils */
import {generateImageSrc} from '../ImageView/ImageViewUtils'

/* Styles */
import s from './VideoView.scss'

const VideoView = (props) => {
  const {
    className,
    video: {
      mp4,
    },
    play,
    poster,
    posterSize,
    onPlayChange,
    disablePictureInPicture,
    enableFullScreen,
    showPlayIcon,
    showCloseIcon,
    activeOverlay,
    onCloseView,
    ...otherProps
  } = props

  const ref = useRef();

  useEffect(() => {
    document.addEventListener('fullscreenchange', fullScreenChanged);
    ref?.current?.addEventListener('play', (e) => {
      onPlayChange?.(true)
    });

    ref?.current?.addEventListener('pause', (e) => {
      onPlayChange?.(false)
    });
  }, [])

  const fullScreenChanged = (e) => {
    if (!document.fullscreenElement) {
      videoPause()

      if(enableFullScreen && ref.current) {
        ref.current.currentTime = 0
      }
    }
  }

  useEffect(() => {
    if (play) {
      videoPlay();
    }
    if (!play) {
      videoPause();
    }
  }, [play]);

  const videoPlay = () => {
    ref?.current?.play();
    if(ref?.current?.requestFullscreen) {
      videoPlayFullScreen()
    }
  }

  const videoPlayFullScreen = () => {
    if (enableFullScreen) {
      ref?.current?.requestFullscreen().catch((err) => null)
    }
  }

  const videoPause = () => {
    ref?.current?.pause();
  }

  const clickVideoPlayer = () => {
    if (enableFullScreen) {
      // eslint-disable-next-line no-unused-expressions
      play ? videoPause() : videoPlay()
    }
  }

  const posterImage = useMemo(() => {
    const imageSrc = generateImageSrc(poster?.url, {
      width: posterSize?.width,
      height: posterSize?.height,
    });

    return imageSrc?.png;
  }, [poster, posterSize])

  if (!mp4) {
    return null;
  }

  return (
    <>
      <div className={cx(s.root, className)} onClick={() => clickVideoPlayer()}>
        <video
          ref={ref}
          disablePictureInPicture={disablePictureInPicture}
          playsInline={false}
          poster={posterImage}
          {...otherProps}>
          {
            mp4 &&
            <source
              src={mp4}
              type={VIDEO_TYPES.MP4}/>
          }
          {HTML5_VIDEO_NOT_SUPPORTED_TEXT}
        </video>
        {
          showPlayIcon && !play &&
          <div
            className={s.playIcon}
            onClick={() => clickVideoPlayer()}
            data-test-id={'video-play-Icon'}/>
        }
        {
          showCloseIcon &&
          <div
            className={s.closeButton} onClick={() => {
              onCloseView()
              onPlayChange?.(false)
            }}/>
        }
      </div>
      <Overlay isActive={activeOverlay}/>
    </>
  );
}

VideoView.propTypes = {
  className: PropTypes.string,
  disablePictureInPicture: PropTypes.bool,
  enableFullScreen: PropTypes.bool,
  showPlayIcon: PropTypes.bool,
  poster: PropTypes.string,
  posterSize: PropTypes.string
}

VideoView.defaultProps = {
  disablePictureInPicture: true,
  showPlayIcon: true,
  enableFullScreen: true,
  showCloseIcon: false
}

export default VideoView
